import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "./firebase";

const inflightCache = new Map();
const CACHE_FAIL_KEY = "avatarCacheFail";
const CACHE_FAIL_TTL_MS = 24 * 60 * 60 * 1000;

export const getUserProfile = async (uid) => {
  if (!uid) return null;
  const snap = await getDoc(doc(db, "user_profiles", uid));
  return snap.exists() ? snap.data() : null;
};

export const ensureAvatarCached = async ({ uid, photoURL }) => {
  if (!uid || !photoURL) return "";

  const failKey = `${CACHE_FAIL_KEY}:${uid}`;
  const lastFail = Number(localStorage.getItem(failKey) || "0");
  if (lastFail && Date.now() - lastFail < CACHE_FAIL_TTL_MS) {
    return "";
  }

  const inflightKey = `${uid}:${photoURL}`;
  if (inflightCache.has(inflightKey)) {
    return inflightCache.get(inflightKey);
  }

  const profileRef = doc(db, "user_profiles", uid);
  const existingSnap = await getDoc(profileRef);
  if (existingSnap.exists()) {
    const existing = existingSnap.data();
    if (existing.photoUrl && existing.sourcePhotoUrl === photoURL) {
      return existing.photoUrl;
    }
  }

  const cachingPromise = (async () => {
    const response = await fetch(photoURL);
    if (!response.ok) {
      localStorage.setItem(failKey, Date.now().toString());
      throw new Error("Failed to download avatar image.");
    }
    const blob = await response.blob();
    const contentType = blob.type || "image/jpeg";
    const extension = contentType.split("/")[1] || "jpg";
    const storageRef = ref(storage, `user-avatars/${uid}.${extension}`);

    await uploadBytes(storageRef, blob, { contentType });
    const downloadUrl = await getDownloadURL(storageRef);

    await setDoc(
      profileRef,
      {
        photoUrl: downloadUrl,
        sourcePhotoUrl: photoURL,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return downloadUrl;
  })()
    .catch(() => "")
    .finally(() => {
      inflightCache.delete(inflightKey);
    });

  inflightCache.set(inflightKey, cachingPromise);
  return cachingPromise;
};
